import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { useModal } from './ModalContext'

const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' } },
}

function CheckIcon() {
  return (
    <svg
      className="w-4 h-4 text-accent-primary flex-shrink-0 mt-[2px]"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
    </svg>
  )
}

function PlanCard({ plan, onCta }) {
  const ctaText = `Reservar com ${plan.name}`

  return (
    <motion.div
      className={`relative flex flex-col bg-surface-card rounded-card p-6 overflow-hidden ${
        plan.highlighted
          ? 'border-[3px] border-accent-primary shadow-xl'
          : 'border border-border-default shadow-card'
      }`}
      variants={cardVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: '-100px' }}
    >
      <div className="relative z-10 flex flex-col h-full">
        <h3 className="text-[20px] font-bold text-text-primary mb-1">{plan.name}</h3>
        <p className="text-[13px] text-text-muted mb-5 pr-12">{plan.description}</p>

        <div className="flex items-end gap-1 mb-5">
          <span className="text-[32px] md:text-[34px] font-extrabold text-accent-primary leading-none">
            R$ {plan.price_monthly}
          </span>
          <span className="text-[14px] font-medium text-text-secondary mb-1">/mês</span>
        </div>

        <button
          aria-label={`${ctaText} — abre formulário de cadastro`}
          onClick={onCta}
          className={`${plan.highlighted ? 'btn-primary' : 'btn-ghost'} w-full mb-5 !py-2.5 !text-[14px]`}
        >
          {ctaText}
        </button>

        <div className="flex flex-col gap-3">
          {plan.features.map((feat, idx) => (
            <div key={idx} className="flex items-start gap-2.5">
              <CheckIcon />
              <span
                className={`text-[14px] ${
                  plan.highlighted ? 'text-text-primary font-medium' : 'text-text-secondary'
                }`}
              >
                {feat}
              </span>
            </div>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

export default function Pricing() {
  const [plans, setPlans] = useState([])
  const [loading, setLoading] = useState(true)
  const { openModal } = useModal()

  useEffect(() => {
    fetch('/api/v1/plans')
      .then((r) => r.json())
      .then((data) => setPlans(data))
      .finally(() => setLoading(false))
  }, [])

  return (
    <section id="planos" className="w-full bg-surface-section-alt py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-6 w-full flex flex-col items-center">
        <div className="text-center w-full max-w-[880px] mx-auto mb-6">
          <h2 className="text-[26px] md:text-[32px] font-bold text-text-primary tracking-tight">
            Consultoria jurídica no bolso.
            <br className="hidden md:block" />
            <span className="text-accent-primary">Por menos que 1 consulta.</span>
          </h2>
          <p className="mt-3 text-[15px] md:text-[16px] text-text-secondary max-w-[620px] mx-auto">
            Escolha o plano que faz sentido para o seu momento de carreira.
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col md:flex-row flex-wrap justify-center gap-6 w-full max-w-[1120px] mx-auto">
            {[0, 1, 2].map((i) => (
              <div key={i} className="w-full md:w-[340px] h-80 bg-surface-card rounded-card border border-border-default animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="flex flex-col md:flex-row flex-wrap justify-center gap-6 w-full max-w-[1120px] mx-auto">
            {plans.map((plan) => (
              <div key={plan.id} className="w-full md:w-[340px]">
                <PlanCard plan={plan} onCta={() => openModal(plan.name, plan.id)} />
              </div>
            ))}
          </div>
        )}

        <p className="text-center text-sm text-gray-500 mt-8">
          O plano Especialista inclui 1 especialidade. Especialidades adicionais
          por +40% do valor do plano.
        </p>
      </div>
    </section>
  )
}
